// Recursive type representing RHF's dirtyFields structure
export type DirtyFields<T> = {
  [K in keyof T]?: T[K] extends object
    ? T[K] extends Array<unknown>
      ? true // arrays are treated as a whole
      : DirtyFields<T[K]>
    : true;
};

/**
 * Returns a subset of `allValues`, containing only the fields marked as dirty in `dirtyFields`.
 * If an array is marked dirty, the entire array is returned.
 */
export function dirtyValues<T>(dirtyFields: DirtyFields<T> | true, allValues: T): Partial<T> {
  if (dirtyFields === true) {
    return allValues;
  }

  const result = {} as Partial<T>;

  for (const key in dirtyFields) {
    if (!Object.prototype.hasOwnProperty.call(dirtyFields, key)) {
      continue;
    }

    const dirtySubField = dirtyFields[key];
    const value = allValues[key];

    // If the subfield is dirty, recurse or assign directly
    if (dirtySubField === true) {
      result[key] = value;
    } else if (dirtySubField && typeof value === 'object') {
      result[key] = dirtyValues(dirtySubField as DirtyFields<unknown>, value) as T[Extract<keyof T, string>];
    }
  }

  return result;
}
